import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Button, Card, Field, Spinner, TextInput } from '@salonomy/ui';
import { useSalon } from '../../data/salon.js';
import type { ReportRange } from '../../data/reports.js';
import { downloadCsv } from '../../lib/csv.js';
import { addDays, todayInZone } from '../../lib/time.js';

/**
 * WS9 reports — small shared building blocks BOTH tabs (Reports, Money admin) consume. Token-styled, minimal.
 *
 *  - useDateRange()  → a controlled from/to state + the {from,to,tz} ReportRange both report tabs pass to the
 *                      data hooks. Defaults to a sensible last-30-days window in the salon timezone, and
 *                      CLAMPS the inputs (from <= to, span <= 366 days) so the API's 400 is never surfaced raw.
 *  - <DateRange/>    → the from/to input pair, wired to useDateRange's setters with the same clamps.
 *  - <ReportCard/>   → a Card with a title, loading/empty/error states, and an Export-CSV slot.
 *  - <ExportCsvButton/> → the one-click CSV export, wired to lib/csv.ts downloadCsv.
 */

/** The API rejects a range over this many inclusive days (mirror of reports MAX_RANGE_DAYS). */
const MAX_RANGE_DAYS = 366;
const DEFAULT_WINDOW_DAYS = 30;

/** Inclusive day span between two "YYYY-MM-DD" dates (from..to). */
function spanDays(from: string, to: string): number {
  return (
    Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1
  );
}

export interface UseDateRange {
  from: string;
  to: string;
  tz: string;
  /** Today in the salon zone — a natural `max` for both inputs. */
  today: string;
  range: ReportRange;
  setFrom: (v: string) => void;
  setTo: (v: string) => void;
}

/**
 * Controlled date-range state for the report tabs. Defaults to the last 30 days through today in the salon
 * timezone. Setters clamp so the server never sees an invalid window: setting `from` past `to` pulls `to`
 * with it; setting `to` before `from` pulls `from`; and either edit that would exceed 366 inclusive days
 * snaps the OTHER bound to keep the span legal.
 */
export function useDateRange(windowDays = DEFAULT_WINDOW_DAYS): UseDateRange {
  const { data: salon } = useSalon();
  const tz = salon?.timezone ?? 'Europe/London';
  const today = todayInZone(tz);
  const [from, setFromRaw] = useState(() => addDays(today, -windowDays));
  const [to, setToRaw] = useState(today);

  const setFrom = (next: string) => {
    if (!next) return;
    let nf = next;
    let nt = to;
    if (nf > nt) nt = nf; // from can't pass to
    if (spanDays(nf, nt) > MAX_RANGE_DAYS) nf = addDays(nt, -(MAX_RANGE_DAYS - 1));
    setFromRaw(nf);
    if (nt !== to) setToRaw(nt);
  };
  const setTo = (next: string) => {
    if (!next) return;
    let nt = next;
    let nf = from;
    if (nt < nf) nf = nt; // to can't precede from
    if (spanDays(nf, nt) > MAX_RANGE_DAYS) nt = addDays(nf, MAX_RANGE_DAYS - 1);
    setToRaw(nt);
    if (nf !== from) setFromRaw(nf);
  };

  const range = useMemo<ReportRange>(() => ({ from, to, tz }), [from, to, tz]);
  return { from, to, tz, today, range, setFrom, setTo };
}

/** Selectable forward horizons for the future-revenue projection (weeks). Default 12 mirrors the API. */
export const FUTURE_HORIZON_OPTIONS = [
  { value: 4, label: '4 weeks' },
  { value: 8, label: '8 weeks' },
  { value: 12, label: '12 weeks' },
  { value: 26, label: '26 weeks' },
  { value: 52, label: '52 weeks' },
] as const;
const DEFAULT_HORIZON_WEEKS = 12;

export interface UseFutureHorizon {
  tz: string;
  /** Forward-anchored range: `from` is ALWAYS today (the projection only ever looks ahead). */
  range: ReportRange;
  horizonWeeks: number;
  setHorizonWeeks: (weeks: number) => void;
}

/**
 * Forward-looking control for the future-revenue HERO. UNLIKE useDateRange (which defaults to the last 30
 * days — correct for the five HISTORICAL reports), this anchors `from` to TODAY and exposes a forward
 * `horizonWeeks`, because the projection scans `startAt >= today AND startAt < today + horizonWeeks*7d`.
 * Reusing the backward DateRange here would point the "future" projection at the past — the WS-9 defect this
 * fixes. We deliberately do NOT carry a `to` (the endpoint takes from + horizonWeeks, not a from/to pair).
 */
export function useFutureHorizon(defaultWeeks = DEFAULT_HORIZON_WEEKS): UseFutureHorizon {
  const { data: salon } = useSalon();
  const tz = salon?.timezone ?? 'Europe/London';
  const today = todayInZone(tz);
  const [horizonWeeks, setHorizonWeeks] = useState(defaultWeeks);

  // `from` is anchored forward to today; `to` is carried only to satisfy the ReportRange contract and is the
  // forward horizon end (informational — the projection endpoint ignores `to` and uses horizonWeeks).
  const range = useMemo<ReportRange>(
    () => ({ from: today, to: addDays(today, horizonWeeks * 7), tz }),
    [today, horizonWeeks, tz],
  );
  return { tz, range, horizonWeeks, setHorizonWeeks };
}

/** The from/to input pair. Pass the object returned by useDateRange. */
export function DateRange({ control, label }: { control: UseDateRange; label?: string }) {
  return (
    <div>
      {label ? (
        <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>
          {label}
        </div>
      ) : null}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 12,
          maxWidth: 360,
          alignItems: 'end',
        }}
      >
        <Field label="From">
          <TextInput
            type="date"
            value={control.from}
            max={control.to}
            onChange={(e) => control.setFrom(e.target.value)}
          />
        </Field>
        <Field label="To">
          <TextInput
            type="date"
            value={control.to}
            min={control.from}
            max={control.today}
            onChange={(e) => control.setTo(e.target.value)}
          />
        </Field>
      </div>
    </div>
  );
}

/** A row of CSV data the export button serialises (headers + rows; money cells pre-formatted, see lib/csv.ts). */
export interface CsvExport {
  filename: string;
  headers: string[];
  rows: Array<Array<string | number>>;
}

/**
 * One-click CSV export. `data` is null while the report is loading or empty, which disables the button — the
 * builder passes a thunk that maps the SERVER report rows to formatted cells (money via money(), see lib/csv.ts).
 */
export function ExportCsvButton({ data }: { data: CsvExport | null }) {
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={!data}
      onClick={() => {
        if (data) downloadCsv(data.filename, data.headers, data.rows);
      }}
    >
      Export CSV
    </Button>
  );
}

/**
 * A report wrapper: a titled Card that renders the standard loading/empty/error states for the report tabs
 * and exposes an Export-CSV slot in the header. `isLoading`/`isError`/`isEmpty` drive the body; `children`
 * render only when the report has loaded with data. `csv` (optional) wires an ExportCsvButton in the header.
 */
export function ReportCard({
  title,
  subtitle,
  isLoading,
  isError,
  isEmpty,
  emptyLabel = 'No data for this period.',
  errorLabel = 'Couldn’t load this report for this period.',
  csv,
  headerExtra,
  children,
}: {
  title: string;
  subtitle?: ReactNode;
  isLoading?: boolean;
  isError?: boolean;
  isEmpty?: boolean;
  emptyLabel?: string;
  errorLabel?: string;
  csv?: CsvExport | null;
  /** Extra controls beside the title (e.g. a groupBy SegmentedControl). */
  headerExtra?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <Card>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          marginBottom: subtitle ? 4 : 12,
        }}
      >
        <div className="section-title" style={{ margin: 0 }}>
          {title}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {headerExtra}
          {csv !== undefined ? <ExportCsvButton data={csv} /> : null}
        </div>
      </div>
      {subtitle ? (
        <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
          {subtitle}
        </div>
      ) : null}

      {isError ? (
        <div className="muted" style={{ fontSize: 13 }}>
          {errorLabel}
        </div>
      ) : isLoading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
          <Spinner />
          <span className="muted" style={{ fontSize: 13 }}>
            Loading…
          </span>
        </div>
      ) : isEmpty ? (
        <div className="muted" style={{ fontSize: 13 }}>
          {emptyLabel}
        </div>
      ) : (
        children
      )}
    </Card>
  );
}
